import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useAuth } from '../stores/AuthContext'
import { useAI } from '../stores/AIContext'
import { normalizeBuildPlan } from '../utils/planBuilder'
import UserAvatar from './UserAvatar'
import './AIMessage.css'

/**
 * Build a one-line human-readable preview of a suggestion's effect.
 * Used as the diff line under the suggestion description.
 */
function describeSuggestion(s) {
  if (!s) return ''
  switch (s.type) {
    case 'update_project':
      return `Project → ${[
        s.name && `name: "${s.name}"`,
        s.description !== undefined && 'description updated',
        s.startDate && `start: ${s.startDate}`,
        s.timeUnit && `unit: ${s.timeUnit}`,
      ].filter(Boolean).join(', ')}`
    case 'update_description':
      return 'Project description updated'
    case 'add_flow':
      return `Add flow "${s.name}"${s.color ? ` (${s.color})` : ''}`
    case 'update_flow':
      return `Update flow${s.flowId ? ` ${s.flowId}` : ''}: ${[
        s.name && `name → "${s.name}"`,
        s.color && `color → ${s.color}`,
        s.hidden !== undefined && (s.hidden ? 'hide' : 'show'),
      ].filter(Boolean).join(', ')}`
    case 'delete_flow':
      return `Delete flow ${s.flowId || ''}`
    case 'add_checkpoint':
      return `Add checkpoint "${s.name}" (time=${s.time ?? '?'}, distance=${s.distance ?? '?'})`
    case 'update_checkpoint':
      return `Update checkpoint: ${[
        s.name && `name → "${s.name}"`,
        s.time !== undefined && `time → ${s.time}`,
        s.distance !== undefined && `distance → ${s.distance}`,
        s.estimatedDuration !== undefined && `duration → ${s.estimatedDuration}m`,
        s.status && `status → ${s.status}`,
      ].filter(Boolean).join(', ')}`
    case 'update_checkpoint_status':
      return `Set checkpoint status → ${s.status}`
    case 'delete_checkpoint':
      return `Delete checkpoint ${s.checkpointId || ''}`
    case 'add_subtask':
      return `Add subtask "${s.text}"${s.checkpointId ? ` → checkpoint ${s.checkpointId}` : ''}`
    case 'toggle_subtask':
      return `Toggle subtask "${s.text || s.subtaskIndex}"${s.checkpointId ? ` → checkpoint ${s.checkpointId}` : ''}`
    case 'delete_subtask':
      return `Delete subtask "${s.text || s.subtaskIndex}"${s.checkpointId ? ` → checkpoint ${s.checkpointId}` : ''}`
    case 'add_dependency':
      return `Add dependency ${s.fromCheckpointId || ''} → ${s.toCheckpointId || ''}`
    case 'delete_dependency':
      return `Remove dependency ${s.fromCheckpointId || ''} → ${s.toCheckpointId || ''}`
    case 'pin_note':
      return `Pin to memory: "${s.text || ''}"`
    case 'create_plan': {
      const { totalFlows, totalCheckpoints, dependencies } = normalizeBuildPlan(s)
      const deps = dependencies.length ? `, ${dependencies.length} dependencies` : ''
      return `Build plan: ${totalFlows} flows, ${totalCheckpoints} checkpoints${deps}`
    }
    default:
      return s.description || s.type
  }
}

/**
 * Editable fields for a suggestion type, used by the Modify inline editor.
 */
function editableFields(type) {
  switch (type) {
    case 'update_project':
      return [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'startDate', label: 'Start date', type: 'text' },
        { key: 'timeUnit', label: 'Time unit', type: 'text' },
      ]
    case 'update_description':
      return [{ key: 'content', label: 'Description', type: 'textarea' }]
    case 'add_flow':
      return [
        { key: 'name', label: 'Flow name', type: 'text' },
        { key: 'color', label: 'Color', type: 'text' },
      ]
    case 'update_flow':
      return [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'color', label: 'Color', type: 'text' },
      ]
    case 'add_checkpoint':
      return [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'time', label: 'Time', type: 'number' },
        { key: 'distance', label: 'Distance', type: 'number' },
        { key: 'estimatedDuration', label: 'Duration (min)', type: 'number' },
      ]
    case 'update_checkpoint':
      return [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'time', label: 'Time', type: 'number' },
        { key: 'distance', label: 'Distance', type: 'number' },
        { key: 'estimatedDuration', label: 'Duration (min)', type: 'number' },
        { key: 'status', label: 'Status', type: 'text' },
      ]
    case 'update_checkpoint_status':
      return [{ key: 'status', label: 'Status', type: 'text' }]
    case 'add_subtask':
      return [{ key: 'text', label: 'Subtask text', type: 'text' }]
    case 'pin_note':
      return [{ key: 'text', label: 'Note text', type: 'textarea' }]
    default:
      return []
  }
}

export default function AIMessage({ message, onApply, onDismiss, onAnswer }) {
  const { user } = useAuth()
  const { settings } = useAI()
  const avatarMode = settings?.avatarMode || 'initials'

  // Tracks which interview questions have been answered (chosen option text).
  const [answeredQuestions, setAnsweredQuestions] = useState({})

  const handleAnswer = (qi, q, option) => {
    if (answeredQuestions[qi]) return
    setAnsweredQuestions(prev => ({ ...prev, [qi]: option }))
    // Tapping an option sends it immediately. When several questions share a
    // message, prefix with the question so Teddo knows which one was answered.
    const multiple = (message.questions?.length || 0) > 1
    const answer = multiple ? `${q.question} → ${option}` : option
    onAnswer && onAnswer(answer)
  }

  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'

  // Per-suggestion UI state: 'pending' | 'applied' | 'dismissed' | 'modifying' | 'failed'
  const [suggestionStates, setSuggestionStates] = useState(
    () => Object.fromEntries((message.suggestions || []).map((_, i) => [i, 'pending']))
  )
  const [drafts, setDrafts] = useState({})
  const [errors, setErrors] = useState({})

  // Keep `suggestionStates` aligned with the current suggestions array.
  // The parent may pass a fresher `message` (Convex live update, session
  // reload, etc.) without remounting; without this sync, newly-arrived
  // suggestions have no entry in state and clicks on them silently no-op.
  useEffect(() => {
    const count = message.suggestions?.length || 0
    setSuggestionStates(prev => {
      let changed = false
      const next = { ...prev }
      for (let i = 0; i < count; i++) {
        if (next[i] === undefined) {
          next[i] = 'pending'
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [message.suggestions])

  const setState = (i, state) => setSuggestionStates(prev => ({ ...prev, [i]: state }))

  const handleAccept = (s, i, modified = false) => {
    // Treat unknown indices as 'pending' rather than silently dropping the
    // click — the state object may not have caught up with a refreshed
    // `message.suggestions` array yet.
    const cur = suggestionStates[i] || 'pending'
    if (cur !== 'pending' && cur !== 'modifying' && cur !== 'failed') return
    const res = onApply && onApply(s, { modified })
    // applySuggestion returns { ok, reason } synchronously.
    if (res && res.ok === false) {
      setState(i, 'failed')
      setErrors(prev => ({ ...prev, [i]: res.reason || 'Could not apply suggestion' }))
      return
    }
    setErrors(prev => {
      const copy = { ...prev }
      delete copy[i]
      return copy
    })
    setState(i, 'applied')
  }

  const handleDismiss = (s, i) => {
    const cur = suggestionStates[i] || 'pending'
    if (cur !== 'pending' && cur !== 'modifying') return
    setState(i, 'dismissed')
    onDismiss && onDismiss(s)
  }

  const startModify = (s, i) => {
    setDrafts(prev => ({ ...prev, [i]: { ...s } }))
    setState(i, 'modifying')
  }

  const cancelModify = (i) => {
    setDrafts(prev => {
      const copy = { ...prev }
      delete copy[i]
      return copy
    })
    setState(i, 'pending')
  }

  const updateDraft = (i, key, value) => {
    setDrafts(prev => ({ ...prev, [i]: { ...(prev[i] || {}), [key]: value } }))
  }

  const applyModified = (i) => {
    const edited = drafts[i]
    if (!edited) return
    handleAccept(edited, i, true)
  }

  return (
    <div className={`ai-message ${message.role}`}>
      <div className="message-header">
        <div className="message-avatar">
          {isUser
            ? <UserAvatar user={user} mode={avatarMode} size={24} />
            : <img src="/teddo_the_icon_with_transparent.png" alt="Teddo" style={{width:'24px',height:'24px',objectFit:'contain'}} />
          }
        </div>
        <div className="message-author">
          {isUser ? (user?.name || user?.email || 'You') : 'Teddo'}
        </div>
        {isAssistant && message.model && (
          <span className="message-model-badge">{message.model}</span>
        )}
      </div>
      <div className="message-content">
        {isAssistant ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
              code: ({ node, inline, className, children, ...props }) => {
                return !inline ? (
                  <pre className="code-block">
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                ) : (
                  <code className="inline-code" {...props}>
                    {children}
                  </code>
                )
              }
            }}
          >
            {message.content}
          </ReactMarkdown>
        ) : (
          <p>{message.content}</p>
        )}
      </div>
      {message.questions?.length > 0 && (
        <div className="message-questions">
          {message.questions.map((q, qi) => {
            const answered = answeredQuestions[qi]
            return (
              <div key={qi} className={`question-block${answered ? ' answered' : ''}`}>
                <div className="question-text">{q.question}</div>
                <div className="question-options">
                  {(q.options || []).map((opt, oi) => (
                    <button
                      key={oi}
                      type="button"
                      className={`question-option${answered === opt ? ' chosen' : ''}`}
                      disabled={!!answered}
                      onClick={() => handleAnswer(qi, q, opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {message.suggestions?.length > 0 && (
        <div className="message-suggestions">
          {message.suggestions.map((s, i) => {
            const state = suggestionStates[i] || 'pending'
            const draft = drafts[i]
            const fields = editableFields(s.type)
            return (
              <div key={s.id || i} className={`suggestion-block suggestion-${state}`}>
                <div className="suggestion-main">
                  <div className="suggestion-icon">
                    {state === 'applied' ? '✓' : state === 'dismissed' ? '—' : '↑'}
                  </div>
                  <div className="suggestion-body">
                    <div className="suggestion-description">{s.description}</div>
                    <div className="suggestion-diff">{describeSuggestion(s)}</div>
                  </div>
                  {state === 'applied' && <span className="suggestion-status-label">Applied</span>}
                  {state === 'dismissed' && <span className="suggestion-status-label">Dismissed</span>}
                  {state === 'failed' && <span className="suggestion-status-label suggestion-status-failed">Failed</span>}
                </div>

                {state === 'failed' && errors[i] && (
                  <div className="suggestion-error">⚠️ {errors[i]}</div>
                )}

                {(state === 'pending' || state === 'failed') && (
                  <div className="suggestion-actions">
                    <button
                      className="suggestion-btn suggestion-accept"
                      onClick={() => handleAccept(s, i)}
                    >
                      {state === 'failed' ? 'Retry' : 'Accept'}
                    </button>
                    {fields.length > 0 && (
                      <button
                        className="suggestion-btn suggestion-modify"
                        onClick={() => startModify(s, i)}
                      >
                        Modify
                      </button>
                    )}
                    <button
                      className="suggestion-btn suggestion-dismissbtn"
                      onClick={() => handleDismiss(s, i)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {state === 'modifying' && draft && (
                  <div className="suggestion-editor">
                    {fields.map(f => (
                      <label key={f.key} className="editor-field">
                        <span className="editor-label">{f.label}</span>
                        {f.type === 'textarea' ? (
                          <textarea
                            value={draft[f.key] ?? ''}
                            onChange={e => updateDraft(i, f.key, e.target.value)}
                            rows={3}
                          />
                        ) : (
                          <input
                            type={f.type}
                            value={draft[f.key] ?? ''}
                            onChange={e => updateDraft(
                              i,
                              f.key,
                              f.type === 'number'
                                ? (e.target.value === '' ? '' : Number(e.target.value))
                                : e.target.value
                            )}
                          />
                        )}
                      </label>
                    ))}
                    <div className="editor-actions">
                      <button
                        className="suggestion-btn suggestion-accept"
                        onClick={() => applyModified(i)}
                      >
                        Apply modified
                      </button>
                      <button
                        className="suggestion-btn suggestion-dismissbtn"
                        onClick={() => cancelModify(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
